import { State, appendOrRemoveValueWith } from '@btime/state-monad'
import { compose, concat, difference, head, isEmpty, prop, propEq, propOr, uniq } from 'ramda'
import React, { useMemo, useState, useCallback } from 'react'

import { generateContext } from '@btime/react-contexts'

const { Provider, StateContext, ActionsContext } = generateContext()
export const useTableState = () => React.useContext(StateContext) || { selecteds: [] }
export const useIsUsingMultiSelect = () => propOr(false, 'active', React.useContext(StateContext))
export const useTableActions = () =>
  React.useContext(ActionsContext) || { setData: () => {}, removeSelecteds: () => {} }

// TODO: Use reducer
const MultiSelectTableContext = ({ children }) => {
  const [{ data, rowKey }, setData] = useState({ data: [], rowKey: 'id' })
  const [selecteds, setSelecteds] = useState([])
  const [isForcingSelection, setForceSelection] = useState(false)

  const selected = useMemo(() => data.find(propEq('id', head(selecteds))), [selecteds.length === 1])
  const allSelected = useMemo(() => {
    const ids = data.map(prop(rowKey))
    return compose(isEmpty, difference(ids))(selecteds)
  }, [data, selecteds])

  const getSelectedsData = useCallback(() => data.filter(item => selecteds.includes(item.id)), [data, selecteds])

  const setUniqSelecteds = useCallback(newState => setSelecteds(uniq(newState)), [setSelecteds])
  const mergeSelecteds = useCallback(
    newState => setUniqSelecteds(concat(selecteds, newState)),
    [setUniqSelecteds, selecteds]
  )

  const removeSelecteds = useCallback(
    ids => setUniqSelecteds(selecteds.filter(id => !ids.includes(id))),
    [setUniqSelecteds, selecteds]
  )

  const handleSelect = useCallback(
    id => () => {
      setForceSelection(false)
      setSelecteds(
        State.of(selecteds)
          .map(appendOrRemoveValueWith(selectedId => selectedId === id, id))
          .fold()
      )
    },
    [selecteds, setSelecteds, setForceSelection]
  )

  const clearSelection = useCallback(() => {
    setSelecteds([])
    setForceSelection(false)
  }, [setSelecteds])

  const selectAll = useCallback(
    isActive => {
      setForceSelection(false)
      const ids = data.map(prop(rowKey))
      return isActive ? mergeSelecteds(ids) : removeSelecteds(ids)
    },
    [rowKey, data]
  )

  const toggleForceSelection = useCallback(() => {
    if (!isForcingSelection) {
      selectAll(false)
    }
    setForceSelection(!isForcingSelection)
  }, [setForceSelection, isForcingSelection])

  const state = { selecteds, selected, allSelected, isForcingSelection, active: true }
  const actions = {
    clearSelection,
    removeSelecteds,
    selectAll,
    toggleForceSelection,
    handleSelect,
    setData,
    getSelectedsData
  }

  return (
    <Provider state={state} actions={actions}>
      {children}
    </Provider>
  )
}

export default MultiSelectTableContext
